import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { HomeFeedItem } from './homeTypes';
import apiService from '../../services/ApiService';

const VARIANT_ACCENT: Record<string, string> = {
  urgent: 'border-l-orange-500',
  time_sensitive: 'border-l-amber-500',
  upcoming: 'border-l-sky-500',
  passport: 'border-l-emerald-500',
  build: 'border-l-zinc-400',
  event: 'border-l-sky-500',
  club: 'border-l-violet-400',
  shop: 'border-l-teal-400',
  organizer: 'border-l-orange-400',
  judge: 'border-l-rose-400',
  system: 'border-l-zinc-500',
  watched: 'border-l-zinc-500',
  completed: 'border-l-emerald-600',
};

const VARIANT_ICON_BG: Record<string, string> = {
  urgent: 'bg-orange-500/15 text-orange-300',
  time_sensitive: 'bg-amber-500/15 text-amber-300',
  upcoming: 'bg-sky-500/15 text-sky-300',
  passport: 'bg-emerald-500/15 text-emerald-300',
  build: 'bg-zinc-500/15 text-zinc-300',
  event: 'bg-sky-500/15 text-sky-300',
  club: 'bg-violet-500/15 text-violet-300',
  shop: 'bg-teal-500/15 text-teal-300',
  organizer: 'bg-orange-500/15 text-orange-300',
  judge: 'bg-rose-500/15 text-rose-300',
  system: 'bg-zinc-500/15 text-zinc-400',
  watched: 'bg-zinc-500/15 text-zinc-300',
  completed: 'bg-emerald-500/15 text-emerald-300',
};

function categoryLabel(item: HomeFeedItem): string {
  if (item.status_label) return item.status_label;
  const cat = (item.category || item.variant || 'update').replace(/_/g, ' ');
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

function presentationOf(item: HomeFeedItem): string {
  return (item.presentation || 'compact').toLowerCase();
}

function FeedGlyph({ variant, type, size = 'md' }: { variant?: string | null; type?: string; size?: 'sm' | 'md' | 'lg' }) {
  const key = (variant || '').toLowerCase();
  const t = (type || '').toLowerCase();
  const tone =
    VARIANT_ICON_BG[key] ||
    (t.includes('passport') || t.includes('qr') ? VARIANT_ICON_BG.passport : VARIANT_ICON_BG.system);

  let paths: string[] = ['M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'];
  if (key === 'passport' || t.includes('passport') || t.includes('qr') || t.includes('scan')) {
    paths = ['M12 4v1m6 11h2m-6.5 0H6.5A2.5 2.5 0 014 13.5v-7A2.5 2.5 0 016.5 4h11A2.5 2.5 0 0120 6.5v3', 'M9 12h6m-3-3v6'];
  } else if (key === 'event' || key === 'upcoming' || key === 'time_sensitive') {
    paths = ['M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'];
  } else if (key === 'club') {
    paths = [
      'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    ];
  } else if (key === 'shop') {
    paths = [
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    ];
  } else if (key === 'build' || key === 'watched' || t.includes('badge') || t.includes('fav')) {
    paths = ['M5 13l4 4L19 7'];
  }

  const box = size === 'lg' ? 'h-16 w-16' : size === 'sm' ? 'h-9 w-9' : 'h-12 w-12';
  const icon = size === 'lg' ? 'h-8 w-8' : size === 'sm' ? 'h-4 w-4' : 'h-6 w-6';

  return (
    <div className={`flex ${box} shrink-0 items-center justify-center rounded-md ${tone}`} aria-hidden>
      <svg className={icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {paths.map((d) => (
          <path key={d} strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={d} />
        ))}
      </svg>
    </div>
  );
}

function ProgressBar({ progress }: { progress: { percent: number; label?: string } }) {
  return (
    <div className="mt-2.5" aria-label={`Progress ${progress.label || `${progress.percent}%`}`}>
      <div className="flex justify-between text-[11px] text-zinc-500 mb-1">
        <span>Progress</span>
        <span>{progress.label || `${Math.round(progress.percent)}%`}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full bg-orange-500/80 transition-[width] duration-500 motion-reduce:transition-none"
          style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%` }}
        />
      </div>
    </div>
  );
}

interface HomeFeedCardProps {
  item: HomeFeedItem;
  onDismiss?: (item: HomeFeedItem) => void;
  /** Grid span hint for editorial composition (client may override). */
  spanClass?: string;
}

export const HomeFeedCard: React.FC<HomeFeedCardProps> = ({ item, onDismiss, spanClass }) => {
  const navigate = useNavigate();
  const presentation = presentationOf(item);
  const accent = VARIANT_ACCENT[item.variant || ''] || VARIANT_ACCENT[item.category || ''] || 'border-l-zinc-600';
  const href = item.primary_action?.web_url || item.web_url || item.deep_link || null;
  const isUnread = item.read_state !== 'read' || item.action_required;
  const imageSrc = item.image_url ? apiService.getFileUrl(item.image_url) : null;

  const open = () => {
    void apiService.trackEvent('home_feed_item_opened', {
      metadata: {
        item_key: item.item_key,
        type: item.type,
        category: item.category,
        presentation,
      },
    });
    if (item.read_state !== 'read') {
      void apiService.setHomeFeedState('read', item.item_key).catch(() => undefined);
    }
    if (href) navigate(href);
  };

  const label = [categoryLabel(item), item.headline, item.time_label || '', item.action_required ? 'Action required' : '']
    .filter(Boolean)
    .join('. ');

  const actions = (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {href ? (
        <button
          type="button"
          onClick={open}
          className="min-h-[40px] px-3 rounded-md bg-white/[0.08] hover:bg-white/[0.12] text-sm text-white font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        >
          {item.primary_action?.label || 'Open'}
        </button>
      ) : null}
      {item.secondary_action?.web_url ? (
        <button
          type="button"
          onClick={() => {
            void apiService.trackEvent('home_feed_action_completed', {
              metadata: { item_key: item.item_key, action: 'secondary' },
            });
            navigate(item.secondary_action!.web_url!);
          }}
          className="min-h-[40px] px-3 rounded-md text-sm text-zinc-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        >
          {item.secondary_action.label}
        </button>
      ) : null}
      {item.dismissible && onDismiss ? (
        <button
          type="button"
          onClick={() => onDismiss(item)}
          className="ml-auto min-h-[40px] px-2 text-xs text-zinc-500 hover:text-zinc-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
          aria-label={`Dismiss ${item.headline}`}
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );

  const metaRow = (
    <div className="flex items-start justify-between gap-2">
      <p className="spec-label text-[11px] tracking-wide text-zinc-500">
        {categoryLabel(item)}
        {item.reason ? <span className="text-zinc-600"> · {item.reason}</span> : null}
        {item.time_label ? <span className="text-zinc-600"> · {item.time_label}</span> : null}
      </p>
      {item.action_required ? (
        <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-orange-300 bg-orange-500/10 border border-orange-500/20">
          Action
        </span>
      ) : null}
    </div>
  );

  // Hero / photo: image-led editorial module
  if ((presentation === 'hero' || presentation === 'photo') && imageSrc) {
    return (
      <article
        className={`group relative overflow-hidden rounded-xl border border-white/[0.08] bg-[#0e0e0e] ${spanClass || ''} ${
          isUnread ? '' : 'opacity-90'
        }`}
        aria-label={label}
        data-presentation={presentation}
      >
        <button type="button" onClick={open} className="block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400">
          <div className={`relative ${presentation === 'hero' ? 'aspect-[16/9] sm:aspect-[21/9]' : 'aspect-[4/3]'} bg-zinc-900`}>
            <img src={imageSrc as string} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" aria-hidden />
            <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
              {metaRow}
              <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-white leading-tight text-balance">
                {item.headline}
              </h3>
              {item.body ? <p className="mt-1.5 text-sm text-zinc-300 line-clamp-2 max-w-xl">{item.body}</p> : null}
            </div>
          </div>
        </button>
        <div className="px-4 pb-3 sm:px-5">{actions}</div>
      </article>
    );
  }

  // Stat / countdown: number-forward tile
  if (presentation === 'stat' || presentation === 'countdown') {
    const big =
      item.progress?.label ||
      item.time_label ||
      (item.progress ? `${Math.round(item.progress.percent)}%` : null) ||
      categoryLabel(item);
    return (
      <article
        className={`rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#141414] to-[#0c0c0c] px-4 py-4 ${spanClass || ''} ${
          isUnread ? '' : 'opacity-90'
        }`}
        aria-label={label}
        data-presentation={presentation}
      >
        <p className="spec-label text-zinc-500">{categoryLabel(item)}</p>
        <p className="stat-numeral mt-2 text-3xl sm:text-4xl text-white tracking-tight">{big}</p>
        <h3 className="mt-2 font-display text-base font-medium text-white leading-snug">{item.headline}</h3>
        {item.body ? <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{item.body}</p> : null}
        {item.progress ? <ProgressBar progress={item.progress} /> : null}
        {actions}
      </article>
    );
  }

  // Progress tile
  if (presentation === 'progress') {
    return (
      <article
        className={`rounded-xl border border-white/[0.08] bg-[#0e0e0e]/95 px-4 py-4 ${spanClass || ''}`}
        aria-label={label}
        data-presentation="progress"
      >
        {metaRow}
        <h3 className="mt-2 font-display text-lg font-medium text-white">{item.headline}</h3>
        {item.body ? <p className="mt-1 text-sm text-zinc-400">{item.body}</p> : null}
        {item.progress ? <ProgressBar progress={item.progress} /> : null}
        {actions}
      </article>
    );
  }

  // Wide / medium with optional side image
  if (presentation === 'wide' || presentation === 'medium') {
    return (
      <article
        className={`group relative border-l-2 ${accent} rounded-r-xl border border-white/[0.06] bg-[#0e0e0e]/90 overflow-hidden ${spanClass || ''} ${
          isUnread ? '' : 'opacity-80'
        }`}
        aria-label={label}
        data-presentation={presentation}
      >
        <div className={`flex ${presentation === 'wide' && imageSrc ? 'flex-col sm:flex-row' : 'gap-3'} p-3.5 sm:p-4`}>
          {imageSrc ? (
            <img
              src={imageSrc as string}
              alt=""
              loading="lazy"
              className={
                presentation === 'wide'
                  ? 'w-full sm:w-44 h-36 sm:h-auto object-cover rounded-lg bg-zinc-900 shrink-0'
                  : 'h-16 w-16 shrink-0 rounded-md object-cover bg-zinc-900'
              }
            />
          ) : (
            <FeedGlyph variant={item.variant} type={item.type} size={presentation === 'wide' ? 'lg' : 'md'} />
          )}
          <div className="min-w-0 flex-1 mt-3 sm:mt-0">
            {metaRow}
            <h3 className="mt-1 font-display text-base sm:text-lg font-medium text-white leading-snug text-balance">
              {item.headline}
            </h3>
            {item.body ? <p className="mt-1 text-sm text-zinc-400 leading-relaxed line-clamp-3">{item.body}</p> : null}
            {item.progress ? <ProgressBar progress={item.progress} /> : null}
            {actions}
          </div>
        </div>
      </article>
    );
  }

  // Square image tile
  if (presentation === 'square' && imageSrc) {
    return (
      <article
        className={`overflow-hidden rounded-xl border border-white/[0.08] bg-[#0e0e0e] ${spanClass || ''}`}
        aria-label={label}
        data-presentation="square"
      >
        <button type="button" onClick={open} className="block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400">
          <div className="aspect-square relative bg-zinc-900">
            <img src={imageSrc as string} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" aria-hidden />
            <div className="absolute inset-x-0 bottom-0 p-3">
              <p className="spec-label text-[10px] text-zinc-400">{categoryLabel(item)}</p>
              <h3 className="font-display text-sm font-medium text-white line-clamp-2">{item.headline}</h3>
            </div>
          </div>
        </button>
        {item.dismissible && onDismiss ? (
          <div className="px-2 py-1.5 flex justify-end">
            <button
              type="button"
              onClick={() => onDismiss(item)}
              className="min-h-[36px] px-2 text-xs text-zinc-500"
              aria-label={`Dismiss ${item.headline}`}
            >
              Dismiss
            </button>
          </div>
        ) : null}
      </article>
    );
  }

  // Compact / inline default
  return (
    <article
      className={`group relative border-l-2 ${accent} rounded-r-lg border border-white/[0.06] bg-[#0e0e0e]/90 px-3.5 py-3 sm:px-4 sm:py-3.5 transition-colors hover:border-white/10 ${spanClass || ''} ${
        isUnread ? '' : 'opacity-80'
      }`}
      aria-label={label}
      data-presentation={presentation === 'inline' ? 'inline' : 'compact'}
    >
      <div className="flex gap-3">
        {imageSrc ? (
          <img src={imageSrc as string} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-md object-cover bg-zinc-900" />
        ) : (
          <FeedGlyph variant={item.variant} type={item.type} />
        )}
        <div className="min-w-0 flex-1">
          {metaRow}
          <h3 className="mt-1 font-display text-[15px] sm:text-base font-medium text-white leading-snug text-balance">
            {item.headline}
          </h3>
          {item.body ? <p className="mt-1 text-sm text-zinc-400 leading-relaxed line-clamp-2">{item.body}</p> : null}
          {item.progress ? <ProgressBar progress={item.progress} /> : null}
          {actions}
        </div>
      </div>
    </article>
  );
};

/** Desktop grid span classes from presentation. */
export function feedItemSpanClass(presentation?: string | null): string {
  const p = (presentation || 'compact').toLowerCase();
  if (p === 'hero') return 'md:col-span-12';
  if (p === 'wide' || p === 'photo') return 'md:col-span-8';
  if (p === 'medium' || p === 'countdown' || p === 'progress') return 'md:col-span-6';
  if (p === 'square' || p === 'stat') return 'md:col-span-4';
  return 'md:col-span-12';
}

export default HomeFeedCard;
